import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import StadiumRoundedIcon from "@mui/icons-material/StadiumRounded";
import {
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import React, { useState } from "react";
import { CircleFlag } from "../../common/CircleFlag";
import { useBets } from "../../common/contexts/betContext";
import { useUser } from "../../common/contexts/userContext";
import { Bet } from "../../types/Bet";
import { Match } from "../../types/Match";
import { getMatchBettingState, getMatchKickoff } from "../../utils/betting";
import countryDict from "../../utils/countryDict";
import { matchVenueLabel } from "../../utils/venue";
import { ModalValue, initModalValue } from "./BetModalBody";
import { submitBet } from "./betSubmit";
import "./styles.scss";

interface BetDialogProps {
  open: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  bet?: Bet;
  match?: Match;
  /** Match used purely for display (names, flags, stage, kickoff). */
  displayMatch?: Match;
}

function flagCode(name: string | undefined): string {
  return (name && countryDict.get(name)) || "pl";
}

function stageLabel(match?: Match): string {
  if (!match) return "";
  const { type, value } = match.matchType;
  return type === "Group" ? `Group ${value}` : value;
}

function kickoffLabel(match?: Match): string {
  const date = getMatchKickoff(match);
  if (!date) return "";
  return date.toLocaleString([], {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const BetDialog: React.FC<BetDialogProps> = ({
  open,
  setIsOpen,
  bet,
  match,
  displayMatch,
}) => {
  const { user } = useUser();
  const { bets, setBets } = useBets();

  // The user's current prediction — passed directly as `bet`, or looked up by
  // match id when the dialog is opened from the fixtures list.
  const existingBet =
    bet ?? bets.find((b) => b.matchId === (match?.id ?? displayMatch?.id));

  // Seed with the saved prediction so re-opening shows the numbers the user
  // already bet. The dialog remounts on each open (MUI Dialog unmounts its
  // children when closed), so this re-reads the latest saved bet every time.
  const [modalValue, setModalValue] = useState<ModalValue>(() =>
    existingBet
      ? {
          homeScore: existingBet.homeTeamScoreBet,
          awayScore: existingBet.awayTeamScoreBet,
        }
      : initModalValue
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const homeName = displayMatch?.homeTeam.internationalName || "";
  const awayName = displayMatch?.awayTeam.internationalName || "";
  const stage = stageLabel(displayMatch);
  const venue = displayMatch ? matchVenueLabel(displayMatch) : "";
  const kickoff = kickoffLabel(displayMatch);
  const isComplete =
    modalValue.homeScore !== null && modalValue.awayScore !== null;

  const bettingState = getMatchBettingState(displayMatch);
  const bettingOpen = bettingState === "open";
  const matchAlreadyPlayed = bettingState === "played";

  // Nothing to save when the inputs still match the already-saved prediction.
  const isUnchanged =
    existingBet !== undefined &&
    modalValue.homeScore === existingBet.homeTeamScoreBet &&
    modalValue.awayScore === existingBet.awayTeamScoreBet;

  const canSave = isComplete && !isUnchanged && bettingOpen && !isSubmitting;

  function close() {
    setModalValue(initModalValue);
    setIsOpen(false);
  }

  function updateScore(side: "homeScore" | "awayScore", raw: string) {
    const digits = raw.replace(/[^0-9]/g, "").slice(0, 2);
    setModalValue((prev) => ({
      ...prev,
      [side]: digits === "" ? null : Number(digits),
    }));
  }

  async function handleSave() {
    if (!canSave) return;
    setIsSubmitting(true);
    try {
      await submitBet({ modalValue, bet, match, userId: user.id, bets, setBets });
      close();
    } catch {
      alert("Something went wrong - try bet a match once again");
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={close}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle component="div" sx={{ pb: 1 }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between">
          <div>
            <Typography variant="h6" fontWeight={700}>
              Predict Score
            </Typography>
            <Stack
              direction="row"
              spacing={1}
              useFlexGap
              flexWrap="wrap"
              sx={{ mt: 1 }}
            >
              {stage && (
                <Chip
                  size="small"
                  icon={<StadiumRoundedIcon />}
                  label={stage}
                  variant="outlined"
                />
              )}
              {kickoff && (
                <Chip
                  size="small"
                  icon={<ScheduleRoundedIcon />}
                  label={kickoff}
                  variant="outlined"
                />
              )}
              {venue && (
                <Chip
                  size="small"
                  icon={<PlaceOutlinedIcon />}
                  label={venue}
                  variant="outlined"
                />
              )}
            </Stack>
          </div>
          <IconButton onClick={close} aria-label="Close" edge="end">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={3}
          sx={{ py: 2 }}
        >
          <Team name={homeName} />
          {bettingOpen ? (
            <ScoreField
              label={`${homeName} score`}
              value={modalValue.homeScore}
              placeholder={existingBet?.homeTeamScoreBet ?? undefined}
              onChange={(raw) => updateScore("homeScore", raw)}
            />
          ) : (
            <StaticScore value={existingBet?.homeTeamScoreBet ?? undefined} />
          )}
          <Typography variant="h4" sx={{ color: "text.secondary" }}>
            :
          </Typography>
          {bettingOpen ? (
            <ScoreField
              label={`${awayName} score`}
              value={modalValue.awayScore}
              placeholder={existingBet?.awayTeamScoreBet ?? undefined}
              onChange={(raw) => updateScore("awayScore", raw)}
            />
          ) : (
            <StaticScore value={existingBet?.awayTeamScoreBet ?? undefined} />
          )}
          <Team name={awayName} />
        </Stack>
      </DialogContent>

      {bettingOpen ? (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={close} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckRoundedIcon />}
            disabled={!canSave}
            onClick={handleSave}
          >
            {isSubmitting ? "Saving..." : "Save Bet"}
          </Button>
        </DialogActions>
      ) : (
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ color: "text.secondary" }}
          >
            <LockRoundedIcon fontSize="small" />
            <Typography variant="body2">
              {existingBet
                ? matchAlreadyPlayed
                  ? "Match already played."
                  : "Predictions are locked — this match has started."
                : matchAlreadyPlayed
                ? "You did not place a bet before kickoff. Match already played."
                : "You did not place a bet before kickoff."}
            </Typography>
          </Stack>
          <Button onClick={close} color="inherit">
            Close
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

const Team: React.FC<{ name: string }> = ({ name }) => (
  <Stack alignItems="center" spacing={1} sx={{ width: 110 }}>
    <CircleFlag countryCode={flagCode(name)} height={56} width={56} />
    <Typography variant="body2" fontWeight={600} align="center" noWrap sx={{ maxWidth: "100%" }}>
      {name}
    </Typography>
  </Stack>
);

// Read-only score shown once predictions lock; "–" when the user never bet.
const StaticScore: React.FC<{ value?: number }> = ({ value }) => (
  <Typography
    sx={{
      width: 84,
      textAlign: "center",
      fontSize: "2rem",
      fontWeight: 700,
      color: "text.secondary",
    }}
  >
    {value ?? "–"}
  </Typography>
);

interface ScoreFieldProps {
  label: string;
  value: number | null;
  placeholder?: number;
  onChange: (raw: string) => void;
}

const ScoreField: React.FC<ScoreFieldProps> = ({
  label,
  value,
  placeholder,
  onChange,
}) => (
  <TextField
    type="number"
    aria-label={label}
    value={value ?? ""}
    placeholder={(placeholder ?? 0).toString()}
    onChange={(e) => onChange(e.target.value)}
    inputProps={{
      min: 0,
      max: 99,
      inputMode: "numeric",
      className: "no-spin",
      style: { textAlign: "center", fontSize: "2rem", fontWeight: 700, padding: "8px" },
    }}
    sx={{ width: 84 }}
  />
);

export default BetDialog;
